import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable, OnDestroy } from "@angular/core";
import { LocalStorageService } from "ngx-store";
import { Observable, of, ReplaySubject, Subject, throwError } from "rxjs";
import { catchError, retry, takeUntil, tap } from "rxjs/operators";
import { Run } from "src/app/model/dispatch.model";
import { Page, PageNumber } from "src/app/model/pagination";
import { Url } from "src/app/model/users.model";
import { BreachRequest, BreachResponse, BreachResponseAction, BreachResponseInput, BreachSettings, BreachSettingsInput } from "./breach";

@Injectable({
  providedIn: 'root'
})
export class BreachService implements OnDestroy {
  private url: Url = this.storage.get('url');

  private isDestroyed$ = new Subject<void>();

  private _responseActions$ = new ReplaySubject<BreachResponseAction[]>(1);

  private _settings$ = new ReplaySubject<BreachSettings>(1);

  private _cachedBreachRequestPages: Page<BreachRequest>[] = [];

  private _currentBreachRequestPage$ = new ReplaySubject<Page<BreachRequest>>(1);

  /**
   * Get the breach settings.
   */
  public get settings$() {
    return this._settings$;
  }

  /**
   * Get the current page of breach requests.
   */
  public get currentBreachRequestPage$() {
    return this._currentBreachRequestPage$.asObservable();
  }

  /**
   * Get available breach response actions.
   */
  public get responseActions$() {
    return this._responseActions$;
  }

  /**
   * Load service resources.
   * @param client  Http client.
   * @param storage Storage source.
   */
  public constructor(
    private client : HttpClient,
    private storage: LocalStorageService
  ) {
    this.currentBreachRequestPage$
      .subscribe((currentPage) => {
        const cachedPageIndex = this
          ._cachedBreachRequestPages
          .findIndex((cachedPage) => cachedPage.current_page === currentPage.current_page);

        cachedPageIndex === -1
          ? this._cachedBreachRequestPages.push(currentPage)
          : this._cachedBreachRequestPages.splice(cachedPageIndex, 1, currentPage);
      });
    
    this.getBreachSettings();

    this.getBreachResponseActions();
  }

  /**
   * Handle resources on destruction.
   */
  public ngOnDestroy() {
    this._settings$.complete();
    this._responseActions$.complete();
    this.isDestroyed$.complete();
  }

  /**
   * Load breach settings resource into `this.settings`.
   * @returns Request subscriptiion.
   */
  private getBreachSettings() {
    return this.client
      .get<BreachSettings>(`${this.url.rest}/tms/breaches/settings`)
      .pipe(takeUntil(this.isDestroyed$))
      .subscribe((settings) => {
        this._settings$.next(settings);
        console.debug('Breach settings is fetched.')
      });
  }

  /**
   * Fetch breach requests.
   * @param options Optional query parameters for filtering and what-not.
   * @returns List of breach requests.
   */
  public getBreachRequests(options?: { run?: Run, page?: PageNumber }) {
    // return cached data if available
    if(options?.page) {
      const cachedPage = this._cachedBreachRequestPages.find((cachedPage) => cachedPage.current_page === options.page);

      if(cachedPage) {
        return of(cachedPage);
      }
    }

    let queryParams = new HttpParams()
      .set('include', 'breach-response.breach-response-action,requested-by,run-plan-activity.connote.customer,run-plan-activity.run-action,run-plan-activity.location')
      .set('fields[run-plan-activity]', 'id,connote_id,run_action_id,location_id,start_datetime')
      .set('fields[run-plan-activity.run-action]', 'id,label,display_label')
      .set('fields[run_plan-activity.connote]', 'id,customer_id')
      .set('fields[run-plan-activity.connote.customer]', 'id,company_name');

    if(options?.run) {
      queryParams = queryParams.set('filter[runPlanActivity.run.id]', `${options.run.id}`)
    }

    if(options?.page) {
      queryParams = queryParams.set('page', `${options.page}`);
    }

    return this.client
      .get<Page<BreachRequest>>(`${this.url.rest}/api/breaches`, { params: queryParams })
      .pipe(tap((page) => this._currentBreachRequestPage$.next(page)));
  }

  /**
   * Query available breach response actions.
   */
  private getBreachResponseActions() {
    this.client
      .get<BreachResponseAction[]>(`${this.url.rest}/tms/breaches/actions`)
      .pipe(takeUntil(this.isDestroyed$))
      .subscribe((actions) => this._responseActions$.next(actions));
  }

  public deleteBreachRecord(id:number){
      return this.client.delete<any>(`${this.url.rest}/tms/breaches/runplan/`+`${id}`)
      .pipe(
        retry(1),
        catchError((response) => {
          console.error('Something went wrong while deleting the breach records');
          return throwError(response);
        }),
        takeUntil(this.isDestroyed$)
        )
  }

  public getRunPlanBreaches(id:number): Observable<BreachRequest[]>{
    return this.client.get<any>(`${this.url.rest}/tms/breaches/runPlanBreaches?filter[runPlanActivity.run.id]=${id}&include=breach-response.breach-response-action,breachable,requestedBy,runPlanActivity.location,runPlanActivity.geofence`,)
    }

   public getRunEventBreaches(id:number): Observable<BreachRequest[]>{
       return this.client.get<any>(`${this.url.rest}/tms/breaches/runEventBreaches?filter[runEvent.run.id]=${id}&include=breach-response.breach-response-action`)
    }

  /**
   * Request to update the breach settings from the server.
   * @param update Settings to save.
   * @returns The saved settings, or old settings on error.
   */
  public setBreachSettings(update: BreachSettingsInput) {
    return this.client
      .post<BreachSettings>(`${this.url.rest}/tms/breaches/settings`, update)
      .pipe(
        tap((settings) => {
          this._settings$.next(settings);
          console.debug('Breach settings are applied.');
        }),
        catchError((response) => {
          console.error('Breach settings are not updated.');
          return throwError(response);
        }),
        takeUntil(this.isDestroyed$)
      );
  }

  /**
   * Persist breach response to the server.
   * @param input Breach response.
   * @returns Observable to the HTTP response.
   */
  public createBreachResponse(input: BreachResponseInput) {
    return this.client
    .post<BreachResponse[]>(`${this.url.rest}/tms/breaches/responses`, input)
    .pipe(
      tap((savedResponses) => {
        const modifiedCachedPages: Page<BreachRequest>[] = [];

        this._cachedBreachRequestPages.forEach((cachedPage) => {
          let isCachedPageModified = false;
          cachedPage.data
            .map((cachedRequest) => {
              const requestResponse = savedResponses.find((response) => response.breach_request_id === cachedRequest.id);
              if(requestResponse) {
                cachedRequest.breach_response = requestResponse;
                isCachedPageModified = true;
              }
              else {
                return cachedRequest;
              }
            });

          if(isCachedPageModified) {
            modifiedCachedPages.push(cachedPage);
          }
        });
      }),
      tap((savedResponses) => console.debug(`${savedResponses.length} breach response made.`))
    );
  }
}
